import { Button, type ButtonOwnProps, type SxProps } from '@mui/material';
import type { ButtonType, CustomMuiButtonProps } from '@/interfaces/component/button.interface';
import { getButtonCommonStyles } from '@/app/stylesguides';
import theme from '@/app/theme';

const CustomMuiButton = (props: CustomMuiButtonProps & { type: ButtonType }) => {
  const { label, size, sx, children, type, ...muiButtonProps } = props;

  const isPrimaryButton = type === 'PRIMARY';

  const commonStyle = {
    ...getButtonCommonStyles(size ?? 'l'),
    boxShadow: 'none'
  };

  const secondaryDefaultStyle: SxProps = {
    borderColor: theme.palette.gray[200],
    color: theme.palette.gray[800],
    backgroundColor: 'white',
    transition: 'all 500ms ease',
    ':hover': {
      color: theme.palette.blue[400],
      borderColor: theme.palette.blue[400]
    }
  };

  const getButtonStyle = (buttonType: ButtonType) => {
    switch (buttonType) {
      case 'PRIMARY':
        return {
          ...commonStyle,
          ':hover': { boxShadow: 'none' },
          ...sx
        };
      case 'SECONDARY':
        return {
          ...commonStyle,
          ...secondaryDefaultStyle,
          ...sx
        };
      case 'TEXT':
        return {
          ...commonStyle,
          border: 'none',
          color: theme.palette.gray[800],
          backgroundColor: 'white',
          ...sx
        };
      default:
        return {};
    }
  };

  const getButtonVariant = (buttonType: ButtonType): ButtonOwnProps['variant'] => {
    switch (buttonType) {
      case 'PRIMARY':
        return 'contained';
      case 'SECONDARY':
        return 'outlined';
      case 'TEXT':
        return 'text';
    }
  };

  const buttonProps: ButtonOwnProps = {
    color: isPrimaryButton ? 'primary' : undefined,
    variant: getButtonVariant(type),
    ...muiButtonProps
  };

  return (
    <Button {...buttonProps} sx={getButtonStyle(type)}>
      {children ? children : (label ?? '')}
    </Button>
  );
};

export default CustomMuiButton;
